import { sanitizeReturnPath } from '../../util/safeReturnPath';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { camelize } from '../../util/string';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, getFeaturedListingsProps } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';

import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import skipLogoImg from '../../assets/logo-skip.png';

import {
  Page,
  Heading,
  IconSpinner,
  NamedRedirect,
  LinkTabNavHorizontal,
  Modal,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import {
  getAuthInfoFromCookies,
  getAuthErrorFromCookies,
  getHandleSubmitConfirm,
  getHandleSubmitSignup,
} from './AuthenticationPage.helpers';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import EmailVerificationInfo from './EmailVerificationInfo';
import SocialLoginButtons from './SocialLoginButtons/SocialLoginButtons';

import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import css from './AuthenticationPage.module.css';

const track = (name, props = {}) => {
  try {
    if (typeof window === 'undefined') return;
    window.analytics?.track(name, { platform: 'web', ...props });
    if (window.gtag) window.gtag('event', name, props);
    if (window.dataLayer) window.dataLayer.push({ event: name, ...props });
  } catch (_) {}
};

const getTabHeading = ({ messageId, isSelected }) => (
  <Heading as={isSelected ? 'h1' : 'h2'} rootClassName={css.tab}>
    <FormattedMessage id={messageId} />
  </Heading>
);

const getAuthenticationTabs = ({ isLogin, signupRouteName, userTypeMaybe, fromState }) => [
  {
    text: getTabHeading({ messageId: 'AuthenticationPage.signupLinkText', isSelected: !isLogin }),
    selected: !isLogin,
    linkProps: { name: signupRouteName, params: userTypeMaybe, to: fromState },
  },
  {
    text: getTabHeading({ messageId: 'AuthenticationPage.loginLinkText', isSelected: isLogin }),
    selected: isLogin,
    linkProps: { name: 'LoginPage', to: fromState },
  },
];

const AuthenticationFormErrorMessage = ({ isLogin, idpAuthError, loginError, signupError }) => {
  const translationId =
    isLogin && !!idpAuthError
      ? 'AuthenticationPage.idpAuthFailed'
      : isLogin && !!loginError
      ? 'AuthenticationPage.loginFailed'
      : !!signupError && isSignupEmailTakenError(signupError)
      ? 'AuthenticationPage.signupFailedEmailAlreadyTaken'
      : !!signupError
      ? 'AuthenticationPage.signupFailed'
      : null;

  return translationId ? (
    <div className={css.error}>
      <FormattedMessage id={translationId} />
    </div>
  ) : null;
};

const ResendVerificationErrorMessage = ({ sendVerificationEmailError }) => {
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;
};

const BlankPage = ({ schemaTitle, schemaDescription }) => (
  <Page
    title={schemaTitle}
    schema={{
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      name: schemaTitle,
      description: schemaDescription,
    }}
  >
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <div className={css.spinnerContainer}>
        <IconSpinner />
      </div>
    </LayoutSingleColumn>
  </Page>
);

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const [mounted, setMounted] = useState(false);

  const config = useConfiguration();
  const intl = useIntl();

  useEffect(() => {
    if (authError) Cookies.remove('st-autherror');
    setMounted(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  useEffect(() => {
    track('auth_page_viewed');
  }, []);

  const {
    authInProgress,
    currentUser,
    isAuthenticated,
    location,
    params: pathParams,
    loginError,
    scrollingDisabled,
    signupError,
    confirmError,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    tab = 'signup',
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    pageAssetsData,
    pageAssetsFetchInProgress,
    pageAssetsFetchError,
    staticContext,
  } = props;

  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  // Only an internal path may be returned to (util/safeReturnPath). `from` normally
  // comes from router state set by this app, but the OAuth cookie variant crossed
  // the network, so the same rule applies to both.
  const from = sanitizeReturnPath(locationFrom || authinfoFrom, null);
  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';

  const loginPrefillEmail = (() => {
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('email')) return params.get('email');
      return localStorage.getItem('skip_prefill_email') || '';
    } catch (_) {
      return '';
    }
  })();

  const userTypeInPushState = location.state?.userType || null;
  const userTypeInAuthInfo = isConfirm && authInfo?.userType ? authInfo?.userType : null;
  const userType = pathParams?.userType || userTypeInPushState || userTypeInAuthInfo || null;

  const { userTypes = [], userFields = [] } = config.user;
  const preselectedUserType = userTypes.find(conf => conf.userType === userType)?.userType || null;
  const signupRouteName = !!preselectedUserType ? 'SignupForUserTypePage' : 'SignupPage';
  const userTypeMaybe = preselectedUserType ? { userType: preselectedUserType } : {};
  const fromMaybe = from ? { from } : {};
  const fromState = { state: { ...fromMaybe, ...userTypeMaybe } };
  const show404 = userType && !preselectedUserType;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });

  const shouldRedirectToFrom = isAuthenticated && from;

  // After successful auth, route new signups to purpose selection, returning users to landing/from
  const justSignedUp = (() => {
    try {
      return sessionStorage.getItem('skip_just_signed_up') === '1';
    } catch (_) {
      return false;
    }
  })();

  const primaryPurpose = currentUser?.attributes?.profile?.publicData?.primaryPurpose;

  const shouldRedirectToLandingPage =
    isAuthenticated && currentUserLoaded && !showEmailVerification;

  if (!mounted && shouldRedirectToLandingPage) {
    return <BlankPage schemaTitle={schemaTitle} schemaDescription={schemaDescription} />;
  }

  if (shouldRedirectToFrom && !justSignedUp) {
    return <Redirect to={from} />;
  } else if (shouldRedirectToLandingPage && justSignedUp) {
    try {
      sessionStorage.removeItem('skip_just_signed_up');
    } catch (_) {}
    // A new account still has to choose its purpose, but it must not lose the
    // vehicle it came from: carry the return path through onboarding.
    return (
      <Redirect
        to={from ? { pathname: '/onboarding/purpose', state: { from } } : '/onboarding/purpose'}
      />
    );
  } else if (shouldRedirectToLandingPage && !primaryPurpose && !justSignedUp && !from) {
    return <NamedRedirect name="LandingPage" />;
  } else if (shouldRedirectToLandingPage) {
    return <NamedRedirect name="LandingPage" />;
  } else if (show404) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const termsAndConditions = (
    <TermsAndConditions
      onOpenTermsOfService={() => setTosModalOpen(true)}
      onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
      intl={intl}
    />
  );
  const signupLinkText = intl.formatMessage({ id: 'AuthenticationPage.signupLinkText' });
  const loginLinkText = intl.formatMessage({ id: 'AuthenticationPage.loginLinkText' });

  const showConfirmFormForSSO = !showEmailVerification && isConfirm;
  const showAuthenticationForm = !showEmailVerification && !isConfirm;
  const showLoginForm = showAuthenticationForm && isLogin;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <section className={css.root}>
          <div className={css.pageWrapper}>
            <div className={css.card}>
              <div className={css.brandMark}>
                <img src={skipLogoImg} alt="Skip the Dealerships" className={css.brandLogo} />
              </div>

              {!isLogin && !showEmailVerification && !showConfirmFormForSSO && (
                <>
                  <h1 className={css.pageHeading}>Join Canada's smarter vehicle marketplace</h1>
                  <p className={css.pageSubheading}>
                    Buy, sell or list inventory — all from one secure account.
                  </p>
                </>
              )}

              {showAuthenticationForm && (
                <LinkTabNavHorizontal
                  className={css.tabs}
                  tabs={getAuthenticationTabs({
                    isLogin,
                    signupRouteName,
                    userTypeMaybe,
                    fromState,
                  })}
                  ariaLabel={`${signupLinkText} & ${loginLinkText}`}
                />
              )}

              {showAuthenticationForm && (
                <>
                  <SocialLoginButtons
                    isLogin={isLogin}
                    showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
                    showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                    onGoogleClick={() => track('auth_provider_started', { provider: 'google' })}
                    onFacebookClick={() => track('auth_provider_started', { provider: 'facebook' })}
                    {...fromMaybe}
                    {...userTypeMaybe}
                  />

                  <AuthenticationFormErrorMessage
                    isLogin={isLogin}
                    idpAuthError={authError}
                    loginError={loginError}
                    signupError={signupError}
                  />

                  {showLoginForm ? (
                    <LoginForm
                      className={css.loginForm}
                      onSubmit={values => {
                        track('auth_email_submitted', { mode: 'login' });
                        submitLogin(values);
                      }}
                      inProgress={authInProgress}
                      initialEmail={loginPrefillEmail}
                    />
                  ) : (
                    <SignupForm
                      className={css.signupForm}
                      onSubmit={getHandleSubmitSignup({ submitSignup, userFields, userTypes })}
                      inProgress={authInProgress}
                      termsAndConditions={termsAndConditions}
                      preselectedUserType={preselectedUserType}
                      userTypes={userTypes}
                      userFields={userFields}
                      onEmailStarted={() => track('auth_email_started', { mode: 'signup' })}
                    />
                  )}

                  {!isLogin && (
                    <p className={css.secureStatement}>
                      🔒 Secure registration. Your information is never sold.
                    </p>
                  )}
                </>
              )}

              {showConfirmFormForSSO && (
                <div>
                  <button
                    onClick={() => window.history.back()}
                    style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 22,
                      color: '#6b7280',
                      lineHeight: 1,
                      padding: 4,
                    }}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                  <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
                    <FormattedMessage
                      id="AuthenticationPage.confirmSignupWithIdpTitle"
                      values={{ idp }}
                    />
                  </Heading>
                  <p className={css.confirmInfoText}>
                    <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
                  </p>
                  <AuthenticationFormErrorMessage
                    isLogin={false}
                    idpAuthError={null}
                    loginError={null}
                    signupError={confirmError}
                  />
                  <ConfirmSignupForm
                    className={css.form}
                    inProgress={authInProgress}
                    onSubmit={getHandleSubmitConfirm({
                      authInfo,
                      submitSingupWithIdp,
                      userFields,
                      userTypes,
                    })}
                    termsAndConditions={termsAndConditions}
                    authInfo={authInfo}
                    idp={idp}
                    preselectedUserType={preselectedUserType}
                    userTypes={userTypes}
                    userFields={userFields}
                  />
                </div>
              )}

              {showEmailVerification && (
                <EmailVerificationInfo
                  name={user.attributes.profile.firstName}
                  email={<span className={css.email}>{user.attributes.email}</span>}
                  onResendVerificationEmail={onResendVerificationEmail}
                  resendErrorMessage={
                    <ResendVerificationErrorMessage
                      sendVerificationEmailError={sendVerificationEmailError}
                    />
                  }
                  sendVerificationEmailInProgress={sendVerificationEmailInProgress}
                />
              )}
            </div>
          </div>
        </section>
      </LayoutSingleColumn>

      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={'terms-accepted.tos-and-privacy'}
      >
        <div className={css.termsWrapper} role="complementary">
          <TermsOfServiceContent
            inProgress={pageAssetsFetchInProgress}
            error={pageAssetsFetchError}
            data={pageAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
            featuredListings={getFeaturedListingsProps(camelize(PRIVACY_POLICY_ASSET_NAME), props)}
            isOpen={tosModalOpen}
          />
        </div>
      </Modal>

      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={'terms-accepted.tos-and-privacy'}
      >
        <div className={css.privacyWrapper} role="complementary">
          <PrivacyPolicyContent
            inProgress={pageAssetsFetchInProgress}
            error={pageAssetsFetchError}
            data={pageAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
            featuredListings={getFeaturedListingsProps(camelize(PRIVACY_POLICY_ASSET_NAME), props)}
            isOpen={privacyModalOpen}
          />
        </div>
      </Modal>
    </Page>
  );
};

const AuthenticationPage = props => {
  const dispatch = useDispatch();

  const authInProgress = useSelector(state => authenticationInProgress(state));
  const currentUser = useSelector(state => state.user?.currentUser);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const loginError = useSelector(state => state.auth?.loginError);
  const signupError = useSelector(state => state.auth?.signupError);
  const confirmError = useSelector(state => state.auth?.confirmError);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const sendVerificationEmailInProgress = useSelector(
    state => state.user?.sendVerificationEmailInProgress
  );
  const sendVerificationEmailError = useSelector(state => state.user?.sendVerificationEmailError);

  const hostedAssets = useSelector(state => state.hostedAssets || {});
  const pageAssetsData = hostedAssets.pageAssetsData;
  const pageAssetsFetchInProgress = hostedAssets.inProgress;
  const pageAssetsFetchError = hostedAssets.error;

  const featuredListingData = useSelector(state => state.featuredListings || {});
  const entities = useSelector(state => state.marketplaceData?.entities || {});

  const getListingEntitiesById = useCallback(
    listingIds => getListingsById({ marketplaceData: { entities } }, listingIds),
    [entities]
  );

  const submitLogin = useCallback(({ email, password }) => dispatch(login(email, password)), [
    dispatch,
  ]);
  const submitSignup = useCallback(params => dispatch(signup(params)), [dispatch]);
  const submitSingupWithIdp = useCallback(params => dispatch(signupWithIdp(params)), [dispatch]);
  const onResendVerificationEmail = useCallback(() => dispatch(sendVerificationEmail()), [
    dispatch,
  ]);
  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onFetchFeaturedListings = useCallback(
    (sectionId, parentPage, listingImageConfig, allSections) =>
      dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
    [dispatch]
  );

  return (
    <AuthenticationPageComponent
      {...props}
      authInProgress={authInProgress}
      currentUser={currentUser}
      isAuthenticated={isAuthenticated}
      loginError={loginError}
      scrollingDisabled={scrollingDisabled}
      signupError={signupError}
      confirmError={confirmError}
      submitLogin={submitLogin}
      submitSignup={submitSignup}
      submitSingupWithIdp={submitSingupWithIdp}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      onResendVerificationEmail={onResendVerificationEmail}
      onManageDisableScrolling={onManageDisableScrolling}
      pageAssetsData={pageAssetsData}
      pageAssetsFetchInProgress={pageAssetsFetchInProgress}
      pageAssetsFetchError={pageAssetsFetchError}
      featuredListingData={featuredListingData}
      getListingEntitiesById={getListingEntitiesById}
      onFetchFeaturedListings={onFetchFeaturedListings}
    />
  );
};

export default AuthenticationPage;
